import React, { useContext, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import classNames from 'classnames';

import useApi from 'common/useApi';
import httpClient from 'common/useApi/httpClient';
import AuthContext from 'common/auth';
import UiContext from 'common/UiContext/UiContext';

import FormLabel from 'components/FormLabel';
import Loading from 'components/Loading';

import './AccountData.scss';

// GDPR self-service: download a full export of the account's data, and request
// erasure (which anonymises the account server-side and ends the session).
const AccountData = () => {
  const { user, logout } = useContext(AuthContext)!;
  const { addToast } = useContext(UiContext);
  const [exporting, setExporting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: { confirm: '', token: '', password: '' },
  });
  const { handleSubmit, register, watch } = formMethods;

  // Deterministic gate (RHF's isValid is unreliable before first validation):
  // require the typed confirmation plus a second factor — a 6-digit OTP for 2FA
  // accounts, otherwise the current password (verified server-side).
  const confirmValue = watch('confirm');
  const tokenValue = watch('token');
  const passwordValue = watch('password');
  const secondFactorOk = user?.has_device
    ? (tokenValue ?? '').length === 6
    : (passwordValue ?? '').length > 0;
  const canDelete = confirmValue === 'DELETE' && secondFactorOk;

  const { isError, isSuccess, error, isLoading, mutate } = useApi(
    'post',
    '/api/me/delete-account',
  );

  const downloadExport = () => {
    setExporting(true);
    httpClient
      .get('/api/me/export/', { responseType: 'blob' })
      .then(({ data }) => {
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');

        link.href = url;
        link.download = 'exapool-data-export.json';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => addToast!('Could not export your data. Please, retry'))
      .finally(() => setExporting(false));
  };

  // On success the account is gone and the session is invalidated server-side;
  // clear local auth so the app redirects to login.
  useEffect(() => {
    if (isSuccess) {
      addToast!('Your account has been deleted');
      logout();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      addToast!(error?.data?.error?.[0] ?? 'Could not delete your account', 'Error');
    }
  }, [isError]);

  const onDelete = ({ token, password }: { confirm: string, token: string, password: string }) => {
    mutate(user?.has_device ? { token } : { password });
  };

  return (
    <>
      <h3>Your Data &amp; Privacy</h3>
      <div className={classNames({ box: true, isLoading: isLoading || exporting, 'account-data': true })}>
        {(isLoading || exporting) && <Loading />}

        <div className='account-data-row'>
          <div>
            <strong>Download your data</strong>
            <p>Export everything we store about your account and mining activity as JSON.</p>
          </div>
          <button type='button' className='primary' onClick={downloadExport} disabled={exporting}>
            Download
          </button>
        </div>

        <hr />

        <div className='account-data-row'>
          <div>
            <strong>Delete your account</strong>
            <p>
              Permanently removes your personal data and disables login. Your
              completed payout and reward records are retained (anonymised) as
              required for financial recordkeeping. This cannot be undone.
            </p>
          </div>
          {!confirming && (
            <button type='button' className='danger' onClick={() => setConfirming(true)}>
              Delete account
            </button>
          )}
        </div>

        {confirming && (
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onDelete)} className='account-data-confirm'>
              <FormLabel field='confirm' name='Type DELETE to confirm' className='half'>
                <input
                  type='text'
                  placeholder='DELETE'
                  autoComplete='off'
                  {...register('confirm', { validate: (value) => value === 'DELETE' || 'Type DELETE to confirm' })}
                />
              </FormLabel>
              {user?.has_device ? (
                <FormLabel field='token' name='One-time password from your OTP app' className='half'>
                  <input
                    type='text'
                    placeholder='######'
                    {...register('token', {
                      required: 'Token is required',
                      minLength: { value: 6, message: 'Token must be 6 characters' },
                      maxLength: { value: 6, message: 'Token must be 6 characters' },
                    })}
                  />
                </FormLabel>
              ) : (
                <FormLabel field='password' name='Confirm your password' className='half'>
                  <input
                    type='password'
                    placeholder='Current password'
                    autoComplete='current-password'
                    {...register('password', { required: 'Password is required' })}
                  />
                </FormLabel>
              )}
              <div className='account-data-actions'>
                <button type='button' className='secondary' onClick={() => setConfirming(false)}>
                  Cancel
                </button>
                <button type='submit' className='danger big' disabled={!canDelete || isLoading}>
                  Permanently delete my account
                </button>
              </div>
            </form>
          </FormProvider>
        )}
      </div>
    </>
  );
};

export default AccountData;
